const PICTURE_FIELD_RE =
  /(picture|image|photo|thumbnail|banner|overviewimg|teamimg|thesisimg|backgroundimg|backgroundpicture|extrapicture|profileimg|iconimg)/i

const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|svg|bmp|ico)$/i

export function isPictureFieldName(name: string | undefined | null): boolean {
  return PICTURE_FIELD_RE.test(String(name || ""))
}

export function extractPictureFilename(value: unknown): string {
  if (value == null) return ""
  if (typeof value === "string") return value.trim()
  if (typeof value === "object" && !Array.isArray(value)) {
    const filename = (value as { filename?: unknown }).filename
    if (typeof filename === "string" && filename.trim()) return filename.trim()
  }
  return ""
}

export function looksLikePictureValue(value: unknown): boolean {
  const filename = extractPictureFilename(value)
  if (!filename) return false
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    const mimetype = (value as { mimetype?: unknown }).mimetype
    if (typeof mimetype === "string" && /^image\//i.test(mimetype)) return true
  }
  return IMAGE_EXT_RE.test(filename)
}

function encodeUploadPath(filename: string): string {
  return filename
    .replace(/^\/+/, "")
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/")
}

/** Labwick upload base: VITE_PUBLIC_URL, else same-origin /uploads (Vite dev proxy). */
export function resolveLabwickUploadBase(publicUrl?: string): string {
  const fromEnv = String(publicUrl ?? import.meta.env.VITE_PUBLIC_URL ?? "").replace(/\/+$/, "")
  if (fromEnv) return fromEnv
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin.replace(/\/+$/, "")}/uploads`
  }
  return ""
}

export function resolveLabwickUploadUrl(value: unknown, publicUrl?: string): string {
  const filename =
    typeof value === "string"
      ? value.trim().replace(/^\/+/, "")
      : extractPictureFilename(value)
  if (!filename) return ""
  const base = resolveLabwickUploadBase(publicUrl)
  if (!base) return ""
  return `${base}/${encodeUploadPath(filename)}`
}

export function resolveLabwickImageUrls(
  value: unknown,
  publicUrl?: string
): { src: string; thumb: string } {
  const filename = extractPictureFilename(value)
  if (!filename) return { src: "", thumb: "" }
  const base = resolveLabwickUploadBase(publicUrl)
  if (!base) return { src: "", thumb: "" }
  const encoded = encodeUploadPath(filename)
  const src = `${base}/${encoded}`
  const usesApiUploads = /\/api\/uploads$/i.test(base) || /\/uploads$/i.test(base)
  const thumb = usesApiUploads ? src : `${base}/thumb/${encoded}`
  return { src, thumb }
}

export function resolveConnectorImageUrls(
  value: unknown,
  assetPublicUrl?: string | null
): { src: string; thumb: string } {
  const filename = extractPictureFilename(value)
  if (!filename) return { src: "", thumb: "" }
  const base = String(assetPublicUrl || "").replace(/\/+$/, "")
  if (!base) return { src: "", thumb: "" }
  const url = `${base}/img/${filename}`
  return { src: url, thumb: url }
}

export function resolveFieldImageUrls(
  value: unknown,
  opts: { connectorAssetPublicUrl?: string | null; publicUrl?: string } = {}
): { src: string; thumb: string } {
  if (opts.connectorAssetPublicUrl) {
    return resolveConnectorImageUrls(value, opts.connectorAssetPublicUrl)
  }
  return resolveLabwickImageUrls(value, opts.publicUrl)
}

export function effectiveImageFieldType(
  field: { name?: string; type?: string },
  sampleValue?: unknown
): string {
  const type = String(field?.type || "").trim().toLowerCase()
  if (type === "url" || type === "email") return type
  if (type === "image") return "image"
  if (isPictureFieldName(field?.name)) return "image"
  if (shouldRenderAsPictureCell(field, sampleValue)) return "image"
  return type
}

export function shouldRenderAsPictureCell(
  field: { name?: string; type?: string } | undefined,
  value: unknown
): boolean {
  const type = String(field?.type || "").trim().toLowerCase()
  if (type === "url" || type === "email") return false
  if (type === "image") return true
  if (isPictureFieldName(field?.name)) return true
  if (looksLikePictureValue(value)) return true
  if (isPictureFieldName(field?.name) && extractPictureFilename(value)) return true
  return false
}

export function formatCellDisplayText(value: unknown): string {
  if (value == null) return ""
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value)
  }
  const filename = extractPictureFilename(value)
  if (filename) return filename
  return ""
}

const VEENINGLAB_ASSET_BASE = "https://veeninglab.com/rest"

export function resolveConnectorAssetPublicUrl(meta?: {
  connectorAssetPublicUrl?: string
  source?: { connectorKey?: string }
  slug?: string
  uid?: string
}): string | undefined {
  if (meta?.connectorAssetPublicUrl) return meta.connectorAssetPublicUrl
  const key = meta?.source?.connectorKey
  if (key === "veeninglab" || (typeof key === "string" && /veeninglab/i.test(key))) {
    return VEENINGLAB_ASSET_BASE
  }
  const slug = String(meta?.slug || "")
  if (/veeninglab/i.test(slug)) {
    return VEENINGLAB_ASSET_BASE
  }
  const uid = String(meta?.uid || "")
  if (/^live:veeninglab:/i.test(uid)) {
    return VEENINGLAB_ASSET_BASE
  }
  return undefined
}
